import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Building2, CreditCard } from 'lucide-react';

export default function PaymentModal({
  onClose,
  paymentMethod,
  setPaymentMethod,
  creditOptions,
  resetPaymentMethod,
  accentColor
}) {
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [showCardDropdown, setShowCardDropdown] = useState(false);
  const bankDropdownRef = useRef(null);
  const cardDropdownRef = useRef(null);

  const handleApply = () => {
    if (!paymentMethod.cardId) {
      alert('Por favor selecciona un banco y una tarjeta');
      return;
    }
    onClose();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bankDropdownRef.current && !bankDropdownRef.current.contains(event.target)) {
        setShowBankDropdown(false);
      }
      if (cardDropdownRef.current && !cardDropdownRef.current.contains(event.target)) {
        setShowCardDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get unique banks
  const banks = [...new Set(creditOptions.map(option => option.bank))].sort();

  // Get cards for selected bank
  const cardsForSelectedBank = paymentMethod.bank
    ? creditOptions.filter(option => option.bank === paymentMethod.bank)
    : [];

  const handleBankChange = (bank) => {
    setPaymentMethod({
      ...paymentMethod,
      type: 'tarjeta',
      bank: bank,
      cardId: null // Reset card when bank changes
    });
    setShowBankDropdown(false);
  };

  const handleCardChange = (cardId) => {
    setPaymentMethod({
      ...paymentMethod,
      type: 'tarjeta',
      cardId: cardId
    });
    setShowCardDropdown(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Seleccionar Tarjeta</h2>
            <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-gray-800 w-8 h-8 rounded-full flex items-center justify-center transition">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Bank Selector */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Banco</label>
            <div className="relative" ref={bankDropdownRef}>
              <button
                onClick={() => setShowBankDropdown(!showBankDropdown)}
                className={`w-full h-[56px] flex items-center justify-between gap-3 px-4 bg-white border-2 rounded-2xl font-semibold transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 ${!accentColor ? 'border-purple-100 focus:ring-purple-400' : 'border-gray-200'}`}
                style={accentColor && showBankDropdown ? { ringColor: accentColor } : {}}
              >
                <div className="flex items-center gap-3">
                  <Building2 className={`w-5 h-5 ${!accentColor ? 'text-purple-400' : 'text-gray-400'}`} />
                  <span className="text-gray-800">
                    {paymentMethod.bank ? paymentMethod.bank.toUpperCase() : 'Seleccionar banco'}
                  </span>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showBankDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showBankDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border-2 border-purple-50 overflow-y-auto max-h-[200px] z-50 animate-fadeIn">
                  {banks.map((bank) => (
                    <button
                      key={bank}
                      onClick={() => handleBankChange(bank)}
                      className={`w-full flex items-center px-4 py-4 text-left transition-all hover:bg-purple-50 border-t first:border-t-0 border-gray-50 ${paymentMethod.bank === bank ? 'bg-purple-50 text-purple-700 font-bold' : 'text-gray-700'}`}
                    >
                      <span className="text-sm uppercase tracking-wide">{bank}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Card Selector */}
          {paymentMethod.bank && (
            <div className="mb-8">
              <label className="block text-gray-700 font-semibold mb-3">Tarjeta</label>
              <div className="relative" ref={cardDropdownRef}>
                <button
                  onClick={() => setShowCardDropdown(!showCardDropdown)}
                  className={`w-full h-[56px] flex items-center justify-between gap-3 px-4 bg-white border-2 rounded-2xl font-semibold transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 ${!accentColor ? 'border-purple-100 focus:ring-purple-400' : 'border-gray-200'}`}
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className={`w-5 h-5 ${!accentColor ? 'text-purple-400' : 'text-gray-400'}`} />
                    <span className="text-gray-800">
                      {paymentMethod.cardId
                        ? cardsForSelectedBank.find(c => c.id === paymentMethod.cardId)?.type
                        : 'Seleccionar tarjeta'}
                    </span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showCardDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showCardDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border-2 border-purple-50 overflow-y-auto max-h-[200px] z-50 animate-fadeIn">
                    {cardsForSelectedBank.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleCardChange(option.id)}
                        className={`w-full flex items-center px-4 py-4 text-left transition-all hover:bg-purple-50 border-t first:border-t-0 border-gray-50 ${paymentMethod.cardId === option.id ? 'bg-purple-50 text-purple-700 font-bold' : 'text-gray-700'}`}
                      >
                        <span className="text-sm font-semibold">{option.type}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            onClick={handleApply}
            className={`w-full py-4 text-white rounded-2xl font-bold text-lg transition shadow-xl ${!accentColor ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : 'hover:opacity-90'}`}
            style={accentColor ? { backgroundColor: accentColor } : {}}
          >
            Aplicar Selección
          </button>
        </div>
      </div>
    </div>
  );
}
